import {Box, Button} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {PageHeader} from 'src/components/ui';
import {useCallback, useEffect, useState} from 'react';
import AddDeviceModal from 'src/components/Modals/AddDevice';
import SearchApartments from 'src/components/SearchApartment';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {IntercomDevice} from 'src/apis/autoGenerate/types/intercomDevice';
import {selectDevice} from 'src/redux/slice/deviceSlice';
import {useAppSelector} from 'src/redux/hooks';
import Spin from 'src/components/Spin';
import SearchInput from 'src/components/SearchInput';
import {intercomDeviceServiceFind} from 'src/apis/autoGenerate/services';
import {useOrgId, useCanEdit} from 'src/hooks';
import IntercomDeviceListGrid from 'src/components/IntercomDeviceListGrid';

export default function Devices() {
  const devices = useAppSelector(selectDevice);
  const {orgId} = useOrgId();
  const canEdit = useCanEdit();
  const [addDeviceModalOpen, setAddDeviceModalOpen] = useState(false);
  const [searchApartment, setSearchApartment] = useState<Apartment | null>();
  const [searchDevice, setSearchDevice] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [intercomDevices, setIntercomDevices] = useState<IntercomDevice[]>([]);

  const handleSelectApartment = (val: Apartment | null) => {
    setSearchApartment(val ?? undefined);
  };

  const getList = useCallback(
    async (
      orgIdParam = '',
      ids?: string[],
      name?: string,
      apartmentIds?: string[]
    ) => {
      const effectiveOrgId = orgIdParam || orgId;

      setLoading(true);
      try {
        const res = await intercomDeviceServiceFind(effectiveOrgId, ids, name, apartmentIds);

        if (res.data && res.data.intercomDevices) {
          const list = res.data.intercomDevices;
          setIntercomDevices(list?.length ? list : []);
        } else {
          setIntercomDevices([]);
        }
      } catch {
        setIntercomDevices([]);
      } finally {
        setLoading(false);
      }
    },
    [orgId]
  );

  useEffect(() => {
    const apartmentFilter = searchApartment?.id ? [searchApartment.id] : undefined;
    getList(orgId, undefined, searchDevice, apartmentFilter);
  }, [searchApartment, searchDevice, orgId, getList]);

  const handleSearchDeviceByInfo = (device = '') => {
    setSearchDevice(device);
  };

  const refreshGrid = useCallback(() => {
    const apartmentFilter = searchApartment?.id ? [searchApartment.id] : undefined;
    getList(orgId, undefined, searchDevice, apartmentFilter);
  }, [getList, orgId, searchDevice, searchApartment]);

  const handleDeviceModalConfirm = useCallback(() => {
    setAddDeviceModalOpen(false);
    refreshGrid();
  }, [refreshGrid]);

  return (
    <Box>
      <Spin spinning={devices.loadingList} />
      <>
        <PageHeader
          title="Devices"
          actions={
            canEdit ? (
              <Button
                variant="contained"
                onClick={() => setAddDeviceModalOpen(true)}
                startIcon={<AddIcon />}
              >
                Add Device
              </Button>
            ) : undefined
          }
        />
        <Box sx={{display: 'flex', gap: 1.5, mb: 2}}>
          <SearchInput placeholder="Search devices" onSearch={handleSearchDeviceByInfo} />
          <SearchApartments onChange={handleSelectApartment} />
        </Box>
        <Box mt={2}>
          <IntercomDeviceListGrid
            rows={intercomDevices}
            loading={loading}
            orgId={orgId}
            canEdit={canEdit}
            onRefresh={refreshGrid}
            checkboxSelection
            emptyMessage="No devices"
          />
        </Box>
      </>
      {addDeviceModalOpen && (
        <AddDeviceModal
          initData={undefined}
          open={addDeviceModalOpen}
          handleCancel={() => {
            setAddDeviceModalOpen(false);
          }}
          handleConfirm={handleDeviceModalConfirm}
        />
      )}
    </Box>
  );
}
